import React from 'react';
import { ActionIcon, Box, Group, Text } from '@mantine/core';
import { IconMinus, IconPlus, IconTrash } from '@tabler/icons-react';
import { CartLine } from '@common/merch';
import { THEME_COLORS } from '../../theme/ThemeConstants';
import { formatPrice } from '../../utilities/Currency';

interface CartItemProps {
  item: CartLine;
  isLoading: boolean;
  isUpdateLoading: boolean;
  isRemoving: boolean;
  animationType?: 'add' | 'remove';
  animationDuration: number;
  onUpdateQuantity: (lineId: string, quantity: number) => void;
  onRemove: (lineId: string) => void;
}

export const CartItem: React.FC<CartItemProps> = ({
  item,
  isLoading,
  isUpdateLoading,
  isRemoving,
  animationType,
  animationDuration,
  onUpdateQuantity,
  onRemove
}) => {
  const imageUrl = item.merchandise.product.featuredImage?.url;
  const productTitle = item.merchandise.product.title;
  const variantTitle = item.merchandise.title;
  const price = parseFloat(item.merchandise.price.amount);
  const currency = item.merchandise.price.currencyCode;

  return (
    <Box
      style={{
        backgroundColor: THEME_COLORS.background.secondary,
        borderRadius: 8,
        border: `1px solid ${THEME_COLORS.border.primary}`,
        padding: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        opacity: isRemoving ? undefined : (isLoading ? 0.6 : 1),
        transition: isRemoving ? 'none' : 'opacity 0.2s ease',
        animation: animationType
          ? `cartItem${animationType === 'add' ? 'PopIn' : 'PopOut'} ${animationDuration}ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards`
          : 'none'
      }}
    >
      <Box
        style={{
          width: 48,
          height: 48,
          backgroundColor: imageUrl ? 'transparent' : THEME_COLORS.background.primary,
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          overflow: 'hidden'
        }}
      >
        {imageUrl ? (
          <img 
            src={imageUrl} 
            alt={productTitle} 
            style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
          />
        ) : (
          <Text size='xs' fw={600} c={THEME_COLORS.text.primary}>
            {productTitle.substring(0, 2).toUpperCase()}
          </Text>
        )}
      </Box>

      <Box style={{ flex: 1 }}>
        <Text size='sm' fw={700} c={THEME_COLORS.text.primary}>
          {productTitle}
        </Text>
        {variantTitle !== 'Default Title' && (
          <Text size='xs' c={THEME_COLORS.text.secondary}>
            {variantTitle}
          </Text>
        )}
        <Text size='sm' c={THEME_COLORS.text.secondary} fw={500}>
          {item.quantity} x {formatPrice(price, currency)}
        </Text>
      </Box>

      {!isRemoving && (
        <>
          <Group gap={4} style={{ flexShrink: 0 }}>
            <ActionIcon
              variant='filled'
              size='md'
              onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
              disabled={isLoading || item.quantity <= 1}
              loading={isUpdateLoading}
              style={{
                backgroundColor: THEME_COLORS.background.primary,
                color: THEME_COLORS.text.primary,
                border: `1px solid ${THEME_COLORS.border.primary}`,
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => !isLoading && item.quantity > 1 && (e.currentTarget.style.opacity = '0.7')}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              <IconMinus size={16} />
            </ActionIcon>

            <ActionIcon
              variant='filled'
              size='md'
              onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
              disabled={isLoading}
              loading={isUpdateLoading}
              style={{
                backgroundColor: THEME_COLORS.background.primary,
                color: THEME_COLORS.text.primary,
                border: `1px solid ${THEME_COLORS.border.primary}`,
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => !isLoading && (e.currentTarget.style.opacity = '0.7')}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              <IconPlus size={16} />
            </ActionIcon>
          </Group>

          <ActionIcon
            variant='transparent'
            size='md'
            onClick={() => onRemove(item.id)}
            disabled={isLoading}
            style={{
              color: '#ef4444',
              transition: 'opacity 0.2s ease',
              flexShrink: 0
            }}
            onMouseEnter={(e) => !isLoading && (e.currentTarget.style.opacity = '0.7')}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            <IconTrash size={20} />
          </ActionIcon>
        </>
      )}
    </Box>
  );
};
